import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { StatCard, ProgressBar, ActivityItem } from "../components/CRMWidgets";
import { AddressBook, Handshake, ListChecks, ClockCounterClockwise, CurrencyDollar } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { CHART_TOOLTIP_STYLE } from "../config/uiHelpers";

const CRMDashboard = ({ user, logout }) => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/crm/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch CRM dashboard:", error);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const pipelineChartData = dashboard ? [
    { name: "Prospect", value: dashboard.pipeline?.prospect?.value || 0, count: dashboard.pipeline?.prospect?.count || 0 },
    { name: "Negotiation", value: dashboard.pipeline?.negotiation?.value || 0, count: dashboard.pipeline?.negotiation?.count || 0 },
    { name: "Proposal", value: dashboard.pipeline?.proposal?.value || 0, count: dashboard.pipeline?.proposal?.count || 0 },
    { name: "Won", value: dashboard.pipeline?.closed_won?.value || 0, count: dashboard.pipeline?.closed_won?.count || 0 },
    { name: "Lost", value: dashboard.pipeline?.closed_lost?.value || 0, count: dashboard.pipeline?.closed_lost?.count || 0 }
  ] : [];

  const stats = [
    { label: "Total Contacts", value: dashboard?.total_contacts || 0, icon: AddressBook, color: "#002FA7" },
    { label: "Total Deals", value: dashboard?.total_deals || 0, icon: Handshake, color: "#10B981" },
    { label: "Pipeline Value", value: `₹${(dashboard?.total_pipeline_value || 0).toLocaleString('en-IN')}`, icon: CurrencyDollar, color: "#F59E0B" },
    { label: "Pending Tasks", value: dashboard?.pending_tasks || 0, icon: ListChecks, color: "#DC2626" }
  ];

  const getActivityIcon = (type) => {
    switch(type) {
      case 'call': return '//';
      case 'email': return '@';
      case 'meeting': return '[]';
      default: return '#';
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      
      <div className="flex-1 ml-64">
        <div className="p-8">
          <div className="mb-8">
            <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
              CRM Dashboard
            </h1>
            <p className="text-base leading-relaxed text-slate-700">
              Customer relationship management overview
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <>
              {/* Stats */}
              <div data-testid="crm-stats" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {stats.map((stat, index) => (
                  <StatCard key={stat.label} icon={stat.icon} label={stat.label} value={stat.value} color={stat.color} testId={`crm-stat-${index}`} />
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {/* Pipeline Chart */}
                <div data-testid="pipeline-chart" className="lg:col-span-2 bg-white border border-slate-200 rounded-none shadow-sm p-6">
                  <h3 className="text-xl sm:text-2xl tracking-tight leading-tight font-semibold mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>
                    Deal Pipeline
                  </h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={pipelineChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="name" stroke="#475569" />
                      <YAxis stroke="#475569" />
                      <Tooltip
                        contentStyle={CHART_TOOLTIP_STYLE}
                        formatter={(value) => [`₹${value.toLocaleString('en-IN')}`, 'Value']}
                      />
                      <Bar dataKey="value" fill="#002FA7" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Task Summary */}
                <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
                  <h3 className="text-xl sm:text-2xl tracking-tight leading-tight font-semibold mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>
                    Tasks Overview
                  </h3>
                  <div className="space-y-4">
                    <ProgressBar label="Pending" value={dashboard?.pending_tasks || 0} total={dashboard?.total_tasks} color="#F59E0B" />
                    <ProgressBar label="In Progress" value={dashboard?.in_progress_tasks || 0} total={dashboard?.total_tasks} color="#002FA7" />
                    <ProgressBar label="Completed" value={dashboard?.completed_tasks || 0} total={dashboard?.total_tasks} color="#10B981" />
                  </div>

                  <div className="mt-6 pt-4 border-t border-slate-200">
                    <button
                      data-testid="view-all-tasks-btn"
                      onClick={() => navigate('/crm/tasks')}
                      className="w-full rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-4 py-2 font-medium text-sm transition-colors duration-300"
                    >
                      View All Tasks
                    </button>
                  </div>
                </div>
              </div>

              {/* Quick Actions + Recent Activities */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
                  <h3 className="text-xl sm:text-2xl tracking-tight leading-tight font-semibold mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>
                    Quick Actions
                  </h3>
                  <div className="space-y-3">
                    <button data-testid="goto-contacts-btn" onClick={() => navigate('/crm/contacts')} className="w-full rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium transition-colors duration-300 text-left flex items-center gap-3">
                      <AddressBook size={20} weight="fill" /> Manage Contacts
                    </button>
                    <button data-testid="goto-deals-btn" onClick={() => navigate('/crm/deals')} className="w-full rounded-none bg-transparent border border-slate-300 text-slate-800 hover:bg-slate-50 px-6 py-3 font-medium transition-colors duration-300 text-left flex items-center gap-3">
                      <Handshake size={20} /> Manage Deals
                    </button>
                    <button data-testid="goto-tasks-btn" onClick={() => navigate('/crm/tasks')} className="w-full rounded-none bg-transparent border border-slate-300 text-slate-800 hover:bg-slate-50 px-6 py-3 font-medium transition-colors duration-300 text-left flex items-center gap-3">
                      <ListChecks size={20} /> Manage Tasks
                    </button>
                    <button data-testid="goto-activities-btn" onClick={() => navigate('/crm/activities')} className="w-full rounded-none bg-transparent border border-slate-300 text-slate-800 hover:bg-slate-50 px-6 py-3 font-medium transition-colors duration-300 text-left flex items-center gap-3">
                      <ClockCounterClockwise size={20} /> Activity Log
                    </button>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
                  <h3 className="text-xl sm:text-2xl tracking-tight leading-tight font-semibold mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>
                    Recent Activities
                  </h3>
                  {dashboard?.recent_activities?.length > 0 ? (
                    <div className="space-y-4">
                      {dashboard.recent_activities.slice(0, 5).map((activity) => (
                        <ActivityItem key={activity.activity_id} activity={activity} />
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">No recent activities</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CRMDashboard;
